import { useEffect, useState } from "react";
import { ENTRYPOINT } from "config/api";

function transformPath(path: string): string {
  const extension = ["nii.gz", "nii", "json"].find((ext) =>
    path.endsWith("." + ext),
  );
  if (!extension) return path;
  return path
    .replace(/\/(\d+\/\d+\/[^/]+)\//, "/$1/" + extension + "/")
    .replace("." + extension, "");
}

/** Pull the `ses-XXXX` segment out of any path or URL. */
function sessionFromPath(s?: string | null): string | null {
  if (!s) return null;
  const m = s.match(/\/(ses-[^/]+)\//) ?? s.match(/(ses-[^/]+)/);
  return m ? m[1] : null;
}

/** Just the digits, e.g. "ses-20240901" -> "20240901", "2024-09-01" -> "20240901". */
function digitsOnly(s?: string | null): string {
  return s ? String(s).replace(/\D/g, "") : "";
}

export interface MicrobleedUrlsState {
  companionUrl: string | null;
  /** segm-cmb_spatial-analysis volume URL (for voxel->world + spatialLabel). */
  spatialVolumeUrl: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * @param t2starBaseUrl  The T2* base image URL currently shown
 *                       (viewerState.niftiBases.t2starmri). We read the session
 *                       from it so the companion / volume always match the
 *                       displayed base — this is more reliable than `acquisition`
 *                       whose string format can differ from the path's `ses-…`.
 */
export default function useMicrobleedUrls(
  patientId: string | undefined,
  acquisition: string | undefined,
  t2starBaseUrl?: string | null,
): MicrobleedUrlsState {
  const [state, setState] = useState<MicrobleedUrlsState>({
    companionUrl: null,
    spatialVolumeUrl: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (!patientId || !acquisition) return;
    const ac = new AbortController();
    setState((s) => ({ ...s, isLoading: true, error: null }));

    fetch(`${ENTRYPOINT}/patients/${patientId}/disk_items/${acquisition}`, {
      method: "GET",
      credentials: "include",
      signal: ac.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      })
      .then((data) => {
        if (ac.signal.aborted) return;

        const paths: string[] = Array.isArray(data)
          ? data
          : data?.items ?? data?.disk_items ?? data?.files ?? [];

        // The disk_items list contains EVERY session for this patient, so we
        // must pick the anchor for the session currently on screen. Prefer the
        // session embedded in the displayed T2* base URL; fall back to matching
        // the acquisition's digits against each path's `ses-XXXX` segment.
        const baseSession = sessionFromPath(t2starBaseUrl);
        const acqDigits = digitsOnly(acquisition);

        const matchesCurrentSession = (p: string): boolean => {
          if (baseSession) return p.includes(`/${baseSession}/`);
          const m = p.match(/\/ses-(\d{6,})\//);
          return !!m && !!acqDigits && m[1] === acqDigits;
        };

        const cmbAnchor =
          /\/t2starmri\/(?:microbleeds|spatial-lesion-analysis)\/segmentation\/native_space\/[^/]+_(?:label-cmb|segm-cmb)(?:_spatial-analysis)?\.nii(?:\.gz)?$/;

        const anchorPath = paths.find(
          (p) => matchesCurrentSession(p) && cmbAnchor.test(p),
        );

        let companionUrl: string | null = null;
        let spatialVolumeUrl: string | null = null;
        if (anchorPath) {
          const match = anchorPath.match(
            /^(.*\/t2starmri)\/[^/]+\/segmentation\/native_space\/([^/]+?)_(?:label-cmb|segm-cmb)(?:_spatial-analysis)?\.nii(?:\.gz)?$/,
          );
          if (match) {
            const t2starRoot = match[1]; // e.g. /data/1/13/128/ses-20240901/t2starmri
            const subject = match[2]; // e.g. 128

            const companionRawPath = `${t2starRoot}/spatial-lesion-analysis/companion_files/${subject}_segm-cmb_imagingselections.json`;
            companionUrl = `${ENTRYPOINT}${transformPath(companionRawPath)}`;

            const spatialRawPath = `${t2starRoot}/spatial-lesion-analysis/segmentation/native_space/${subject}_segm-cmb_spatial-analysis.nii.gz`;
            spatialVolumeUrl = `${ENTRYPOINT}${transformPath(spatialRawPath)}`;
          }
        }

        setState({
          companionUrl,
          spatialVolumeUrl,
          isLoading: false,
          error: null,
        });
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setState({
          companionUrl: null,
          spatialVolumeUrl: null,
          isLoading: false,
          error: e as Error,
        });
      });

    return () => ac.abort();
  }, [patientId, acquisition, t2starBaseUrl]);

  return state;
}
